"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useConsent, setConsent } from "@/lib/consent";
import { startAnalytics, stopAnalytics } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

// Bandeau de consentement — monté une seule fois au layout racine.
//
// Jamais sur le questionnaire (/formulaire) : la page est à part (un écran par
// question, bouton en bas), et sur téléphone le bandeau recouvrait « C'est parti »
// et avalait le tap. Sans consentement, la mesure PostHog du parcours est
// simplement ignorée — rien n'est déposé.
const HIDDEN_ON = ["/formulaire"];
//
// Deux règles de la CNIL qu'on respecte ici, et qui expliquent le design :
//  · refuser doit être AUSSI simple qu'accepter → un clic chacun, deux boutons de
//    même taille côte à côte, jamais un « paramètres » à déplier pour dire non ;
//  · fermer le bandeau ne vaut pas accord → pas de croix, pas de clic-hors-du-bandeau.
// Ce qui est permis, et sur quoi on joue : le TEXTE (court, honnête, qui dit à quoi
// ça sert), l'identité visuelle (icône, ton du site) et la couleur du bouton d'accord.
const APPEAR_DELAY_MS = 2200;

export function CookieBanner() {
  const t = useT();
  const state = useConsent();
  const reduce = useReducedMotion();
  const pathname = usePathname();
  const hidden = HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  // On laisse le visiteur atterrir avant de lui poser une question : un bandeau
  // servi avant même que la page s'affiche se fait balayer par réflexe. Le délai
  // ne court qu'une fois — rouvrir depuis « Gérer mes cookies » est immédiat.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSettled(true), APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  function accept() {
    setConsent("granted");
    startAnalytics();
  }

  function refuse() {
    setConsent("denied");
    stopAnalytics();
  }

  return (
    <AnimatePresence>
      {state === "unknown" && settled && !hidden && (
        <motion.div
          role="dialog"
          aria-label={t("Cookies de mesure d'audience")}
          className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4"
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
          transition={{ type: "spring", visualDuration: 0.35, bounce: 0.15 }}
          style={{ willChange: "transform" }}
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-border-strong bg-surface/95 p-4 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:gap-5 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                <svg
                  viewBox="0 0 24 24"
                  className="h-[18px] w-[18px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 20V13M10 20V4M16 20v-5M22 20H2" />
                </svg>
              </span>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-fg">{t("Aide-nous à améliorer AtlanX")}</p>
                {/* Espace EXPLICITE : collé à une balise fermante, un simple blanc peut
                    disparaître au rendu (ou à la prochaine remise en forme du fichier),
                    et on lit « du sitepour ». */}
                <p className="text-[0.82rem] leading-relaxed text-fg-dim">
                  {t("Un cookie de mesure, pour voir ce qui sert et corriger ce qui gêne. Aucune publicité, aucune revente — ces chiffres restent chez nous.")}{" "}
                  <Link href="/cookies" className="whitespace-nowrap text-accent hover:underline">
                    {t("En savoir plus")}
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex shrink-0 gap-2">
              <button
                onClick={refuse}
                className="flex-1 cursor-pointer rounded-lg border border-border-strong px-4 py-2.5 text-sm font-medium text-fg-dim transition-colors hover:border-fg-dim hover:text-fg sm:flex-none"
              >
                {t("Refuser")}
              </button>
              <button
                onClick={accept}
                className="flex-1 cursor-pointer rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-on-accent shadow-lg shadow-accent/25 transition-colors hover:bg-accent-hover sm:flex-none"
              >
                {t("Accepter")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
